/* eslint-disable max-len */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import ConfirmDeleteModal from '@components/layout/Modals/ConfirmDeleteModal/ConfirmDeleteModal.component'
import TranscribedAudioPanel from '@components/pages/Notes/TranscribedAudioPanel.component'
import '@pages/Notes/Notes.css'
import '@pages/Notes/NotePdfExport.css'
import './NoteEditorOverlay.css'
import { Patient } from '@dataTypes/PatientInfo.types'
import type { ClaimStatus } from '@dataTypes/claimFeature.types'
import { NoteContextType, type INote } from '@dataTypes/note.type'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { fetchAllNotes } from '@store/actions'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { getFullName } from '@utils/fullname.util'
import type {
  NoteMutationSuccessDetail,
  PreviousPageState,
} from './notesFeature.types'
import { useNotesEditorFeature } from './useNotesEditorFeature'
import NotesFeatureMainColumn from './NotesFeatureMainColumn.component'
import { createFailedToast } from '@utils/notificationToast.util'
import NotesPendingLeaveModal from './NotesPendingLeaveModal.component'

export type NoteEditorOverlayProps = {
  isOpen: boolean
  patient: Patient | null
  onClose: () => void
  contextId?: string | null
  contextType?: NoteContextType | null
  contextName?: string | null
  contactId?: string | null
  openFirstNote?: boolean
  openNoteId?: string | null
  pagePreviousState?: PreviousPageState | null
  /** In overlay mode without router state: NoteEditor back label (e.g. from current pathname). */
  editorBackButtonLabel?: string
  /**
   * In overlay mode without patientPreviousPageState: show context notes sidebar in editor
   * (matches route-driven openContextNotesByDefault).
   */
  openContextNotesByDefault?: boolean
  /**
   * `'add'` bootstraps composer mode.
   * `'list'` opens list/table shell; note view/edit is selected via `openNoteId`,
   * `openFirstNote`, and `openNoteInEditMode`.
   */
  initialView?: 'list' | 'add'
  /** When opening via `openNoteId`, start in the rich-text editor (edit) instead of read-only view. */
  openNoteInEditMode?: boolean
  overlayBackLabel?: string
  /** Extra class on the anchored wrapper (parent should wrap content with position relative or flex) */
  anchorClassName?: string
  /**
   * Global loading target for the initial fetch when the overlay opens (e.g. `'body'` on Charting).
   * When omitted, loading is scoped to the overlay anchor (`.note-editor-overlay-loading-target`).
   */
  notesFetchLoadingContainer?: string
  /** CSS selector for a DOM element to portal the overlay into (e.g. '.page-content'). */
  portalTarget?: string
  /**
   * When false, show the patient notes table in the overlay (search, filters, grid).
   * Default true: editor-only overlay for all callers except those that opt in (e.g. side panel).
   */
  hideNotesTable?: boolean
  /** Called after successful note save/delete mutations within overlay. */
  onNoteMutationSuccess?: (detail?: NoteMutationSuccessDetail) => void
  /** Called synchronously when Save succeeds (before refetch) so hosts can update UI immediately. */
  onNoteSavedImmediate?: (note: INote) => void
  /**
   * When opening claim-context notes (`contextType` claims / claim item): pass `generalStatus ?? status` from the claim.
   * New note and edit are only allowed for `ClaimStatus.Rejected`. If omitted, add/edit are disabled.
   */
  claimStatusForNotes?: ClaimStatus
}

const LOADER_CLASS = 'note-editor-overlay-loading-target'
const LOADER_SELECTOR = `.${LOADER_CLASS}`

const NoteEditorOverlay: React.FC<NoteEditorOverlayProps> = ({
  isOpen,
  patient,
  onClose,
  contextId = null,
  contextType = null,
  contextName = null,
  contactId = null,
  openFirstNote = false,
  openNoteId = null,
  pagePreviousState = null,
  editorBackButtonLabel,
  openContextNotesByDefault: openContextNotesByDefaultProp,
  initialView = 'add',
  openNoteInEditMode = false,
  anchorClassName,
  portalTarget,
  hideNotesTable = true,
  notesFetchLoadingContainer,
  overlayBackLabel = 'Back',
  onNoteMutationSuccess,
  onNoteSavedImmediate,
  claimStatusForNotes,
}) => {
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null)
  useEffect(() => {
    if (!portalTarget) {
      setPortalEl(null)
      return
    }
    const el = document.querySelector<HTMLElement>(portalTarget)
    setPortalEl(el)
  }, [portalTarget])

  useEffect(() => {
    if (!isOpen) return
    document.body.classList.add('note-editor-overlay-active')
    return () => {
      document.body.classList.remove('note-editor-overlay-active')
    }
  }, [isOpen])

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { showToast } = useNotificationToast()
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const {
    data: notes,
    loading: notesLoading,
    error: notesError,
  } = useAppSelector((state) => state.note)
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)

  const lastFetchedPatientIdRef = useRef<string | null>(null)
  const lastContactOrCallFetchKeyRef = useRef<string | null>(null)
  const didBootstrapAddRef = useRef<boolean>(false)

  useEffect(() => {
    if (!isOpen) {
      lastFetchedPatientIdRef.current = null
      lastContactOrCallFetchKeyRef.current = null
      didBootstrapAddRef.current = false
      return
    }
    /**
     * Redux `note.data` is replaced on each fetchAllNotes.fulfilled. Contact/call overlays
     * must load notes for the active context or `firstViewCandidate` sees stale/empty data
     * (e.g. after a patient-scoped fetch elsewhere).
     */
    if (
      contextType === NoteContextType.CONTACT &&
      contactId != null &&
      contactId.trim() !== ''
    ) {
      const key = `contact:${contactId.trim()}`
      if (lastContactOrCallFetchKeyRef.current === key) return
      lastContactOrCallFetchKeyRef.current = key
      void dispatch(
        fetchAllNotes({
          contactId: contactId.trim(),
          contextType: NoteContextType.CONTACT,
        }),
      )
        .unwrap()
        .catch(() => undefined)
      return
    }
    if (
      contextType === NoteContextType.CALL &&
      contextId != null &&
      contextId.trim() !== ''
    ) {
      const key = `call:${contextId.trim()}`
      if (lastContactOrCallFetchKeyRef.current === key) return
      lastContactOrCallFetchKeyRef.current = key
      void dispatch(
        fetchAllNotes({
          contextType: NoteContextType.CALL,
          contextId: contextId.trim(),
        }),
      )
        .unwrap()
        .catch(() => undefined)
      return
    }
    const patientId = patient?.patient_id ?? null
    if (!patientId) {
      lastFetchedPatientIdRef.current = null
      return
    }
    if (lastFetchedPatientIdRef.current === patientId) return
    lastFetchedPatientIdRef.current = patientId
    void dispatch(fetchAllNotes({ patientId }))
      .unwrap()
      .catch(() => undefined)
  }, [isOpen, patient?.patient_id, contextType, contactId, contextId, dispatch])

  const selectedPatientName = useMemo(
    () => (patient ? (getFullName(patient) ?? '') : ''),
    [patient],
  )
  const requiresPatientForCurrentFlow =
    contextType !== NoteContextType.CONTACT &&
    contextType !== NoteContextType.CALL &&
    Boolean(patient?.patient_id)

  const notesLoadingTarget =
    notesFetchLoadingContainer != null &&
    String(notesFetchLoadingContainer).trim() !== ''
      ? String(notesFetchLoadingContainer).trim()
      : LOADER_SELECTOR

  const feature = useNotesEditorFeature({
    isOpen,
    selectedPatient: patient,
    selectedPatientName,
    patientPreviousPageState: pagePreviousState,
    notes,
    notesLoading,
    selectedClinic,
    dispatch,
    showToast,
    showLoading,
    hideLoading,
    navigate,
    originContextId: contextId ?? null,
    originContactId: contactId ?? null,
    originContextType: contextType,
    originContextName: contextName,
    originOpenFirstNote: openFirstNote,
    syncOpenNoteId: openNoteId ?? undefined,
    syncOpenNoteInEditMode: openNoteInEditMode,
    requiresPatientForCurrentFlow,
    loadingContainerSelector: notesLoadingTarget,
    onOverlayClose: onClose,
    editorBackButtonLabel,
    onNoteMutationSuccess,
    onNoteSavedImmediate,
    overlayEditorBackReturnsToList: !hideNotesTable,
    claimStatusForNotes,
  })

  const { handleAddNote: featureHandleAddNote } = feature
  const hasRenderableContext =
    Boolean(patient?.patient_id) ||
    (contextType === NoteContextType.CONTACT &&
      contactId != null &&
      contactId.trim() !== '') ||
    (contextType === NoteContextType.CALL &&
      contextId != null &&
      contextId.trim() !== '') ||
    ((contextType === NoteContextType.CLAIMS ||
      contextType === NoteContextType.CLAIM_ITEM) &&
      contextId != null &&
      String(contextId).trim() !== '') ||
    (contextType === NoteContextType.APPOINTMENT &&
      contextId != null &&
      String(contextId).trim() !== '')
  const canRenderEditorContent =
    feature.isAddingNote ||
    (feature.isEditingNote && feature.editingNote != null) ||
    (feature.isViewingNote && feature.viewingNote != null)

  useEffect((): void => {
    const shouldForceCloseInvalidOverlay =
      isOpen &&
      hideNotesTable &&
      !notesError &&
      !canRenderEditorContent &&
      !hasRenderableContext
    if (!shouldForceCloseInvalidOverlay) {
      return
    }
    showToast(
      createFailedToast(
        'Cannot open notes',
        'Patient or note context is required to open the note editor.',
      ),
    )
    onClose()
  }, [
    isOpen,
    hideNotesTable,
    notesError,
    canRenderEditorContent,
    hasRenderableContext,
    showToast,
    onClose,
  ])

  useEffect(() => {
    if (!isOpen || initialView !== 'add') return
    if (feature.claimContextNotesReadOnly) {
      return
    }
    const allowContextWithoutPatient =
      (contextType === NoteContextType.CONTACT &&
        contactId != null &&
        contactId.trim() !== '') ||
      (contextType === NoteContextType.CALL &&
        contextId != null &&
        contextId.trim() !== '') ||
      ((contextType === NoteContextType.CLAIMS ||
        contextType === NoteContextType.CLAIM_ITEM) &&
        contextId != null &&
        String(contextId).trim() !== '') ||
      (contextType === NoteContextType.APPOINTMENT &&
        contextId != null &&
        String(contextId).trim() !== '')
    if (!patient?.patient_id && !allowContextWithoutPatient) {
      return
    }
    if (didBootstrapAddRef.current) return
    didBootstrapAddRef.current = true
    featureHandleAddNote()
  }, [
    isOpen,
    initialView,
    patient?.patient_id,
    contextType,
    contactId,
    contextId,
    featureHandleAddNote,
    feature.claimContextNotesReadOnly,
  ])

  const transcriptionSegments = feature.transcriptionSegments

  if (!isOpen) {
    return null
  }

  const overlayContent = (
    <div
      className={[
        'note-editor-overlay-anchor',
        portalEl ? 'note-editor-overlay-anchor--portal' : '',
        LOADER_CLASS,
        anchorClassName,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="note-editor-overlay-root">
        <div className="note-editor-overlay-body">
          <div className="notes-page-layout">
            <div className="notes-feature-container sidebar-hidden">
              <div
                className={[
                  'notes-outer',
                  'notes-loading-container',
                  LOADER_CLASS,
                ].join(' ')}
              >
                <div className="notes-inner">
                  <NotesFeatureMainColumn
                    feature={feature}
                    selectedPatient={patient}
                    selectedPatientName={selectedPatientName}
                    patientPreviousPageState={pagePreviousState}
                    originContextId={contextId}
                    originContactId={contactId}
                    originContextType={contextType}
                    originContextName={contextName}
                    originOpenFirstNote={openFirstNote}
                    notesLoading={notesLoading}
                    notesError={notesError ?? undefined}
                    showPatientSideSlot={false}
                    openContextNotesByDefault={openContextNotesByDefaultProp}
                    hideNotesTable={hideNotesTable}
                    overlayListCloseInTableHeader={!hideNotesTable}
                    overlayListCloseLabel={overlayBackLabel}
                    claimContextNotesReadOnly={
                      feature.claimContextNotesReadOnly
                    }
                  />
                </div>
              </div>
            </div>
            {feature.showTranscribedAudio &&
              (feature.isAddingNote || feature.isEditingNote) && (
                <TranscribedAudioPanel segments={transcriptionSegments} />
              )}
          </div>
        </div>
      </div>

      <ConfirmDeleteModal
        show={feature.showDeleteConfirmation}
        onHide={feature.handleCancelDelete}
        onConfirm={feature.handleConfirmDelete}
        entityName={feature.noteToDelete?.title || 'this note'}
      />

      <NotesPendingLeaveModal
        pendingLeave={feature.pendingLeave}
        onDismiss={feature.handleCancelLeavePage}
        onDiscard={feature.handleDiscardAndLeavePage}
        onSave={() => void feature.handleSaveAndLeavePage()}
      />
    </div>
  )

  if (portalEl) {
    return createPortal(overlayContent, portalEl)
  }

  return overlayContent
}

export default NoteEditorOverlay
